import { useNavigate } from 'react-router-dom';
import DriverRankCard from './DriverRankCard/DriverRankCard';
import S from './DriversRankStyles';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useGetAlertsAggregation from '../../API/hooks/alerts/useGetAlertsAggregation';
import { getEndOfTheDay, getFormattedDate } from '../../utils/dateUtils';
import { getFilterFields, getSimpleEqQuery, queryFilterByGroups } from '../../utils/queryFilters';
import GlobalSpinner from '../common/GlobalSpinner/GlobalSpinner';
import useGetDrivers from '../../API/hooks/drivers/useGetDrivers';
import { ComputedScores, useEntityScores } from '../../API/hooks/useEntityScores';
import { DriverDetails } from '../../API/types/Drivers';
import RankCardProps from './DriverRankCard/types';
import DashboardWidgetMenu from '../Dashboard/DashboardWidgetMenu/DashboardWidgetMenu';

const PERIOD_TO_SCORE_FIELD: Record<string, keyof ComputedScores> = {
  daily: 'scoreDaily',
  weekly: 'scoreWeekly',
  monthly: 'scoreMonthly',
};

type ScoredDriver = Omit<DriverDetails, keyof ComputedScores> & ComputedScores;
type ScoreField = keyof ComputedScores;

const getDriverScore = (driver: ScoredDriver, scoreField: ScoreField): number | null => {
  const score = driver[scoreField];
  return score != null && score > 0 ? score : null;
};

const getPeriod = (timeRange: any): string => {
  const raw = timeRange?.period?.[0];
  if (!raw) return 'monthly';
  if (raw === 'custom') return `&from=${timeRange.startDate?.[0]}&to=${timeRange.endDate?.[0]}`;
  return raw;
};

const PERIOD_TO_LABEL: Record<string, string> = {
  daily: 'Today',
  weekly: 'This week',
  monthly: 'This month',
};

const getTitle = (timeRange: any): string => {
  const raw = timeRange?.period?.[0];
  const isAllTime = raw === 'custom' && timeRange?.startDate?.[0] === new Date(0).toISOString();
  if (isAllTime) return 'Drivers Rank • All time';
  if (raw === 'custom') return 'Drivers Rank • Custom period';
  return `Drivers Rank • ${PERIOD_TO_LABEL[raw] ?? 'All time'}`;
};

const getRankCardProps = (
  driver: ScoredDriver | null,
  scoreField: ScoreField,
  alerts: number,
  title: string,
): RankCardProps => {
  if (!driver) {
    return {
      title,
      name: 'N/A',
      since: '',
      truck: 'N/A',
      score: null,
      alerts: 0,
      driverId: undefined,
      avatar: undefined,
    };
  }

  const score = getDriverScore(driver, scoreField);

  return {
    title,
    name: `${driver.firstName} ${driver.lastName}`,
    since: `Since ${getFormattedDate(driver.createdAt || '').toUpperCase()}`,
    truck: driver.vehicle?.licensePlateNumber || 'N/A',
    score: score != null ? Math.round(score) : null,
    alerts,
    driverId: driver._id,
    avatar: driver.avatarUrl,
  };
};

type Props = {
  timeRange: Record<string, string[]>;
  groupFilters?: Record<string, any>;
  onMove?: () => void;
  onRemove?: () => void;
};

function DriversRank({
  timeRange,
  groupFilters,
  onMove,
  onRemove,
}: Props): JSX.Element {
  const fleetId = useGetFleetId();
  const period = getPeriod(timeRange);

  const filters = {
    startDate: [timeRange.startDate?.[0]],
    endDate: [(timeRange.endDate?.[0] || getEndOfTheDay().toISOString())],
    period,
  };

  const scoreField = PERIOD_TO_SCORE_FIELD[timeRange?.period?.[0]] ?? 'score';

  const { data: allDriversData, isLoading } = useGetDrivers(fleetId, `?limit=1000&skip=0${queryFilterByGroups(groupFilters)}`);

  const driverIds = (allDriversData?.results ?? []).map((d) => d._id);
  const { scores: computedDriverScores, isLoading: isScoresLoading } = useEntityScores(fleetId, driverIds, 'driver');

  const driversWithScore = (allDriversData?.results ?? [])
    .flatMap((d) => {
      const cs = computedDriverScores.get(d._id);
      if (!cs) return [];
      const merged: ScoredDriver = { ...d, ...cs };
      return getDriverScore(merged, scoreField) != null ? [merged] : [];
    });
  const topScores = driversWithScore.length > 0
    ? {
      max: driversWithScore.reduce(
        (a, b) => ((a[scoreField] ?? 0) > (b[scoreField] ?? 0) ? a : b),
        driversWithScore[0],
      ),
      min: driversWithScore.reduce(
        (a, b) => ((a[scoreField] ?? 0) < (b[scoreField] ?? 0) ? a : b),
        driversWithScore[0],
      ),
    }
    : { max: null, min: null };

  const { data: topDriverAlerts } = useGetAlertsAggregation(
    fleetId,
    `?limit=1000&skip=0${getSimpleEqQuery('driverId', topScores.max?._id ?? '', 12)}${getFilterFields(filters, ['period'])}${queryFilterByGroups(groupFilters)}`,
    topScores.max != null,
  );
  const { data: bottomDriverAlerts } = useGetAlertsAggregation(
    fleetId,
    `?limit=1000&skip=0${getSimpleEqQuery('driverId', topScores.min?._id ?? '', 12)}${getFilterFields(filters, ['period'])}${queryFilterByGroups(groupFilters)}`,
    topScores.min != null,
  );

  const isCustomPeriod = timeRange?.period?.[0] === 'custom' && timeRange?.startDate?.[0] !== new Date(0).toISOString();
  const hasRankedDrivers = driversWithScore.length > 0;

  const navigate = useNavigate();
  const handleClick = (): void => {
    navigate('/drivers');
  };

  return (
    <S.Wrapper>
      <div className="header">
        <h3 className="header-title">{getTitle(timeRange)}</h3>
        <DashboardWidgetMenu
          onViewAll={handleClick}
          onMove={onMove}
          onRemove={onRemove}
        />
      </div>
      {(isLoading || isScoresLoading) && <GlobalSpinner />}
      {!isLoading && !isScoresLoading && isCustomPeriod && (
        <div style={{ opacity: 0.5, textAlign: 'center', margin: 'auto' }}>
          No data available for custom period
        </div>
      )}
      {!isLoading && !isScoresLoading && !isCustomPeriod && !hasRankedDrivers && (
        <div style={{ opacity: 0.5, textAlign: 'center', margin: 'auto' }}>
          No drivers with score for this period
        </div>
      )}
      {!isLoading && !isScoresLoading && !isCustomPeriod && hasRankedDrivers && (
        <div className="drivers-rank-container">
          <DriverRankCard
            {...getRankCardProps(
              topScores.max,
              scoreField,
              topScores.max ? (topDriverAlerts?.count || 0) : 0,
              'Top performer',
            )}
          />
          <DriverRankCard
            {...getRankCardProps(
              topScores.min,
              scoreField,
              topScores.min ? (bottomDriverAlerts?.count || 0) : 0,
              'Bottom performer',
            )}
          />
        </div>
      )}
    </S.Wrapper>
  );
}

export default DriversRank;
